import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useParsedRecords } from "@/portals/patient/context/ParsedRecordsContext";
import { useFamily } from "@/portals/patient/context/FamilyContext";
import { getMedications, type ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { adaptMedication } from "@/features/patient/medications/utils/adaptMedication";
import type { Medication } from "@/shared/types";

interface MedicationsResult {
  medications: Medication[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

const MedicationsContext = createContext<MedicationsResult | null>(null);

/**
 * The patient's medications across ALL their uploads and syncs, fetched on
 * their own (resource_types=MedicationRequest). They used to be sliced out of
 * ParsedRecordsContext's every-record-type fetch, which for a long history is
 * several MB and slow over a real network — the Medications tab and the
 * dashboard's medication card no longer wait on it.
 *
 * Stays in step with ParsedRecordsContext: whenever that one refreshes
 * (upload finished, review answered, profile switched...) this refetches
 * too, and this one's refresh() refreshes both.
 */
export function MedicationsProvider({ children }: { children: ReactNode }) {
  const { refresh: refreshRecords, refreshToken: recordsToken } = useParsedRecords();
  const { activeDependent } = useFamily();
  const actingAsProfileId = activeDependent?.id;

  const [records, setRecords] = useState<ParsedRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const shownFor = useRef<string | undefined | null>(null); // profile whose meds are on screen

  useEffect(() => {
    let cancelled = false;
    // A spinner only when switching profile / first load; any later refresh
    // swaps the new list in silently.
    if (shownFor.current !== actingAsProfileId) {
      setIsLoading(true);
      setRecords([]);
    }
    getMedications(actingAsProfileId)
      .then((result) => {
        if (cancelled) return;
        setRecords(result);
        setError(null);
        shownFor.current = actingAsProfileId;
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load your medications. Please try again later.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [recordsToken, actingAsProfileId]);

  const medications = useMemo(() => records.map(adaptMedication), [records]);
  const refresh = useCallback(() => refreshRecords(), [refreshRecords]);

  return (
    <MedicationsContext.Provider value={{ medications, isLoading, error, refresh }}>
      {children}
    </MedicationsContext.Provider>
  );
}

export function useMedications(): MedicationsResult {
  const own = useContext(MedicationsContext);
  const { records, isLoading, error, refresh } = useParsedRecords();
  // Without a MedicationsProvider (e.g. a page rendered on its own in a
  // test), derive from the shared records exactly as before.
  const derived = useMemo(
    () => records.filter((r) => r.resourceType === "MedicationRequest").map(adaptMedication),
    [records],
  );
  return own ?? { medications: derived, isLoading, error, refresh };
}
